import type { Uuid } from '@common/types';
import type { ESelectedEntityType, PanelPosition, SelectedEntity } from '@common/types/layout.type';
import { DeviceMode, EDrawerType, PreviewMode } from '@common/types/layout.type';
import type { LayoutState } from './layout.reducer';
import { LAYOUT_FEATURE_KEY } from './layout.reducer';

import { createFeatureSelector, createSelector } from '@ngrx/store';

export const selectLayoutState = createFeatureSelector<LayoutState>(LAYOUT_FEATURE_KEY);

export const selectSelectedEntity = createSelector(
    selectLayoutState,
    (state: LayoutState): SelectedEntity => state.selectedEntity
);
export const selectEntityType = createSelector(
    selectSelectedEntity,
    (selectedEntity: SelectedEntity): ESelectedEntityType => selectedEntity?.entityType
);
export const selectEntityId = createSelector(
    selectSelectedEntity,
    (selectedEntity: SelectedEntity): Uuid => selectedEntity?.entityId
);
export const selectShowSettings = createSelector(
    selectSelectedEntity,
    (selectedEntity: SelectedEntity): boolean => selectedEntity?.showSettings ?? false
);

export const selectPreviewMode = createSelector(
    selectLayoutState,
    (state: LayoutState): PreviewMode => state?.previewMode
);
export const selectIsPreview = createSelector(selectPreviewMode, (previewMode: PreviewMode): boolean =>
    Boolean(previewMode)
);

export const selectIsPreviewMobile = createSelector(
    selectPreviewMode,
    (previewMode: PreviewMode): boolean => previewMode === PreviewMode.mobile
);
export const selectIsPreviewDesktop = createSelector(
    selectPreviewMode,
    (previewMode: PreviewMode): boolean => previewMode === PreviewMode.desktop
);
export const selectIsPreviewTablet = createSelector(
    selectPreviewMode,
    (previewMode: PreviewMode): boolean => previewMode === PreviewMode.tablet
);

export const selectDeviceMode = createSelector(
    selectLayoutState,
    (state: LayoutState): DeviceMode => state?.deviceMode
);

export const selectIsDeviceMobile = createSelector(
    selectDeviceMode,
    (deviceMode: DeviceMode): boolean => deviceMode === DeviceMode.mobile
);
export const selectIsDeviceTablet = createSelector(
    selectDeviceMode,
    (deviceMode: DeviceMode): boolean => deviceMode === DeviceMode.tablet
);
export const selectIsDeviceDesktop = createSelector(
    selectDeviceMode,
    (deviceMode: DeviceMode): boolean => deviceMode === DeviceMode.desktop
);

export const selectDrawerStateType = createSelector(
    selectLayoutState,
    (state: LayoutState): EDrawerType => state.drawerState.drawerType
);

export const selectIsBlockDrawer = createSelector(
    selectDrawerStateType,
    (state: EDrawerType): boolean => state === EDrawerType.block
);
export const selectIsPageDrawer = createSelector(
    selectDrawerStateType,
    (state: EDrawerType): boolean => state === EDrawerType.page
);
export const selectIsAiChatDrawer = createSelector(
    selectDrawerStateType,
    (state: EDrawerType): boolean => state === EDrawerType.aiChat
);
export const selectIsNavigationDrawer = createSelector(
    selectDrawerStateType,
    (state: EDrawerType): boolean => state === EDrawerType.navigation
);

export const selectBlockIndex = createSelector(
    selectLayoutState,
    (state: LayoutState): number => state.drawerState?.blockIndex
);

export const selectIsEntityDragging = createSelector(
    selectLayoutState,
    (state: LayoutState): boolean => state.isEntityDragging
);

export const selectIsColumnWidthChanging = createSelector(
    selectLayoutState,
    (state: LayoutState): boolean => state.isColumnWidthChanging
);

export const selectHideColumnControls = createSelector(
    selectLayoutState,
    (state: LayoutState): boolean => !state.isHideColumnControls
);

/**
 * True whenever an AI reply is in flight OR the AI chat drawer's element picker is armed — the two
 * independent triggers for §7's "preview mode" (`ai-mcp-plan.md`): showing the page the way a
 * visitor would see it (no hover outlines, no "+" add-buttons, no drag handles) while a click should
 * resolve to a block/column/widget id instead of opening its settings panel. Kept as two booleans
 * ORed here, mirroring `isEntityDragging`/`isColumnWidthChanging` below, rather than one shared flag
 * both callers write: `AiSessionService` and `ElementPickerService` start and end independently, and
 * a shared flag would have whichever one exits first turn preview off under the other.
 */
export const selectIsCanvasPreviewActive = createSelector(
    selectLayoutState,
    (state: LayoutState): boolean => state.isAiReplyActive || state.isElementPickerActive
);

export const selectHideControls = createSelector(
    selectIsEntityDragging,
    selectIsColumnWidthChanging,
    selectIsCanvasPreviewActive,
    (isEntityDragging: boolean, isColumnWidthChanging: boolean, isCanvasPreviewActive: boolean): boolean =>
        !isEntityDragging && !isColumnWidthChanging && !isCanvasPreviewActive
);

export const selectSettingsPanelPosition = createSelector(
    selectLayoutState,
    (state: LayoutState): PanelPosition => state.settingsPanelPosition
);
